import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import { Card, CardBody, CardHeader, CardTitle } from 'reactstrap';
import { Row, Col, Button, Input, FormGroup, Label, Badge } from 'reactstrap';
// Using standard HTML table elements
import { getBeneficiaryCards, getProductionStatus } from '../../../../store/actions/CardTemplateActions';
import CardPreview from '../CardPreview/CardPreview';

const CardManager = ({ 
    beneficiaryCards,
    cardProductionStatus,
    loadingStates,
    error,
    getBeneficiaryCards,
    getProductionStatus
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
    const [currentView, setCurrentView] = useState('list'); // list, preview
    const [statusFilter, setStatusFilter] = useState('');

    const handleSearchBeneficiary = async () => {
        if (!searchTerm.trim()) return;

        // Try to parse as beneficiary ID (number) or search by name/card number
        const isNumeric = /^\d+$/.test(searchTerm.trim());
        
        if (isNumeric) {
            const beneficiaryId = parseInt(searchTerm.trim());
            const result = await getBeneficiaryCards(beneficiaryId);
            
            if (result.success && result.data?.length > 0) {
                setSelectedBeneficiary({
                    id: beneficiaryId,
                    name: result.data[0].beneficiaryName || `Bénéficiaire ${beneficiaryId}`,
                    cards: result.data
                });
            } else {
                setSelectedBeneficiary({
                    id: beneficiaryId,
                    name: `Bénéficiaire ${beneficiaryId}`,
                    cards: []
                });
            }
        }
    };

    const handleCardStatusClick = async (cardId) => {
        if (!cardProductionStatus[cardId]) {
            await getProductionStatus(cardId);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'DELIVERED': { color: 'success', text: I18n.t('cardTemplate.status.delivered') },
            'SHIPPED': { color: 'info', text: I18n.t('cardTemplate.status.shipped') },
            'PRINTING': { color: 'warning', text: I18n.t('cardTemplate.status.printing') },
            'PACKAGING': { color: 'warning', text: I18n.t('cardTemplate.status.packaging') },
            'QUALITY_CONTROL': { color: 'primary', text: I18n.t('cardTemplate.status.qualityControl') },
            'DATA_VALIDATION': { color: 'secondary', text: I18n.t('cardTemplate.status.dataValidation') },
            'PHOTO_PROCESSING': { color: 'secondary', text: I18n.t('cardTemplate.status.photoProcessing') },
            'TEMPLATE_APPLICATION': { color: 'secondary', text: I18n.t('cardTemplate.status.templateApplication') },
            'CANCELLED': { color: 'danger', text: I18n.t('cardTemplate.status.cancelled') }
        };

        const config = statusConfig[status] || { color: 'secondary', text: status };
        return <Badge color={config.color}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getFilteredCards = () => {
        if (!selectedBeneficiary?.cards) return [];
        
        return selectedBeneficiary.cards.filter(card => {
            const matchesStatus = !statusFilter || card.status === statusFilter;
            return matchesStatus;
        });
    };

    const renderCardsList = () => {
        const cards = getFilteredCards();

        if (!selectedBeneficiary) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-search fa-4x text-muted mb-3"></i>
                    <h5 className="text-muted">{I18n.t('cardTemplate.searchBeneficiary')}</h5>
                    <p className="text-muted">{I18n.t('cardTemplate.searchBeneficiaryHint')}</p>
                </div>
            );
        }

        if (loadingStates.loadBeneficiaryCards) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{I18n.t('common.loading')}</span>
                    </div>
                </div>
            );
        }

        if (cards.length === 0) {
            return (
                <div className="text-center py-5">
                    <i className="fa fa-credit-card fa-4x text-muted mb-3"></i>
                    <h5 className="text-muted">{I18n.t('cardTemplate.noCards')}</h5>
                    <p className="text-muted">{I18n.t('cardTemplate.noCardsForBeneficiary')}</p>
                </div>
            );
        }

        return (
            <div className="table-responsive">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>{I18n.t('cardTemplate.field.cardNumber')}</th>
                            <th>{I18n.t('cardTemplate.field.status')}</th>
                            <th>{I18n.t('cardTemplate.field.productionDate')}</th>
                            <th>{I18n.t('cardTemplate.field.deliveryDate')}</th>
                            <th>{I18n.t('cardTemplate.field.trackingNumber')}</th>
                            <th>{I18n.t('common.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cards.map((card) => (
                            <tr key={card.id || card.cardId}>
                                <td>
                                    <strong>{card.cardNumber}</strong>
                                </td>
                                <td>
                                    <div 
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleCardStatusClick(card.id || card.cardId)}
                                    >
                                        {getStatusBadge(card.status)}
                                        {loadingStates.getProductionStatus && (
                                            <div className="spinner-border spinner-border-sm ms-2" role="status"></div>
                                        )}
                                    </div>
                                </td>
                                <td>{formatDate(card.productionDate)}</td>
                                <td>{formatDate(card.deliveryDate)}</td>
                                <td>
                                    {card.trackingNumber && (
                                        <code>{card.trackingNumber}</code>
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex">
                                        <Button
                                            color="info"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => {
                                                setCurrentView('preview');
                                            }}
                                        >
                                            <i className="fa fa-eye"></i>
                                        </Button>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => {
                                                // This will be handled by CardPreview component
                                            }}
                                        >
                                            <i className="fa fa-print"></i>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderPreviewView = () => {
        const cards = getFilteredCards();
        return (
            <CardPreview 
                cards={cards}
            />
        );
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5>{I18n.t('common.error')}</h5>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div>
            {/* Search Section */}
            <Card className="mb-4">
                <CardHeader>
                    <CardTitle>{I18n.t('cardTemplate.cardManager.title')}</CardTitle>
                </CardHeader>
                <CardBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>{I18n.t('cardTemplate.field.beneficiaryId')}</Label>
                                <div className="input-group">
                                    <Input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder={I18n.t('cardTemplate.placeholder.searchBeneficiary')}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearchBeneficiary()}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={handleSearchBeneficiary}
                                        disabled={!searchTerm.trim() || loadingStates.loadBeneficiaryCards}
                                    >
                                        {loadingStates.loadBeneficiaryCards ? (
                                            <div className="spinner-border spinner-border-sm" role="status"></div>
                                        ) : (
                                            <i className="fa fa-search"></i>
                                        )}
                                    </Button>
                                </div>
                            </FormGroup>
                        </Col>
                        {selectedBeneficiary && (
                            <Col md={6}>
                                <FormGroup>
                                    <Label>{I18n.t('cardTemplate.filter.status')}</Label>
                                    <Input
                                        type="select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">{I18n.t('common.all')}</option>
                                        <option value="DATA_VALIDATION">{I18n.t('cardTemplate.status.dataValidation')}</option>
                                        <option value="PHOTO_PROCESSING">{I18n.t('cardTemplate.status.photoProcessing')}</option>
                                        <option value="TEMPLATE_APPLICATION">{I18n.t('cardTemplate.status.templateApplication')}</option>
                                        <option value="QUALITY_CONTROL">{I18n.t('cardTemplate.status.qualityControl')}</option>
                                        <option value="PRINTING">{I18n.t('cardTemplate.status.printing')}</option>
                                        <option value="PACKAGING">{I18n.t('cardTemplate.status.packaging')}</option>
                                        <option value="SHIPPED">{I18n.t('cardTemplate.status.shipped')}</option>
                                        <option value="DELIVERED">{I18n.t('cardTemplate.status.delivered')}</option>
                                        <option value="CANCELLED">{I18n.t('cardTemplate.status.cancelled')}</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        )}
                    </Row>

                    {selectedBeneficiary && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                <h6 className="mb-0">
                                    <i className="fa fa-user me-2"></i>
                                    {selectedBeneficiary.name}
                                </h6>
                                <small className="text-muted">
                                    {I18n.t('cardTemplate.cardsCount', { 
                                        count: selectedBeneficiary.cards?.length || 0 
                                    })}
                                </small>
                            </div>
                            <div>
                                <div className="btn-group" role="group">
                                    <Button
                                        color={currentView === 'list' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setCurrentView('list')}
                                    >
                                        <i className="fa fa-list"></i>
                                        {I18n.t('cardTemplate.view.list')}
                                    </Button>
                                    <Button
                                        color={currentView === 'preview' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setCurrentView('preview')}
                                    >
                                        <i className="fa fa-th-large"></i>
                                        {I18n.t('cardTemplate.view.preview')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Results Section */}
            <Card>
                <CardBody>
                    {currentView === 'list' ? renderCardsList() : renderPreviewView()}
                </CardBody>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({
    beneficiaryCards: state.cardTemplate.beneficiaryCards,
    cardProductionStatus: state.cardTemplate.cardProductionStatus,
    loadingStates: state.cardTemplate.loadingStates,
    error: state.cardTemplate.error
});

const mapDispatchToProps = {
    getBeneficiaryCards,
    getProductionStatus
};

export default connect(mapStateToProps, mapDispatchToProps)(CardManager);